import { useState, useEffect, useCallback } from 'react';
import { fetchCustomers, fetchCustomerById } from '../utils/api';

/**
 * 客戶數據處理Hook
 * 用於獲取和管理客戶數據
 * 
 * @param {string} token - 認證令牌
 * @returns {Object} 客戶數據和操作方法
 */
function useCustomer(token) {
    // 狀態管理
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerError, setCustomerError] = useState(null);

    // 獲取所有客戶
    const loadCustomers = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchCustomers(token);
            setCustomers(data);
            setError(null);
        } catch (err) {
            console.error('獲取客戶列表失敗:', err);
            setError('獲取客戶數據時出錯');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // 初始加載
    useEffect(() => {
        if (token) {
            loadCustomers();
        }
    }, [token, loadCustomers]);

    // 根據ID獲取客戶
    const getCustomerById = useCallback(async (customerId) => {
        // console.log('getCustomerById 被調用，客戶ID:', customerId);

        if (!customerId) {
            console.error('客戶ID為空，無法獲取客戶');
            setCustomerError('客戶ID為空');
            return null;
        }

        // 先從本地緩存中查找
        const cachedCustomer = customers.find(customer =>
            customer.id === customerId ||
            customer.id.split('/').pop() === customerId
        );

        if (cachedCustomer) {
            // console.log('從本地緩存中找到客戶:', cachedCustomer);
            setSelectedCustomer(cachedCustomer);
            return cachedCustomer;
        }

        // 如果本地沒有，則從API獲取
        try {
            // console.log('本地緩存中未找到客戶，嘗試從API獲取');
            setCustomerLoading(true);
            const customer = await fetchCustomerById(customerId, token);
            // console.log('從API獲取到客戶:', customer);
            setSelectedCustomer(customer);
            setCustomerError(null);
            return customer;
        } catch (err) {
            // console.error('獲取客戶詳情失敗:', err);
            setCustomerError('獲取客戶詳情時出錯: ' + (err.message || '未知錯誤'));
            return null;
        } finally {
            setCustomerLoading(false);
        }
    }, [customers, token]);

    // 搜索客戶
    const searchCustomers = useCallback((searchTerm) => {
        if (!searchTerm) return customers;

        const term = searchTerm.toLowerCase().trim();
        if (term === '') return customers;

        // 創建搜索詞數組，支持多個關鍵詞搜索
        const searchTerms = term.split(/\s+/);

        return customers.filter(customer => {
            if (!customer) return false;

            // 預先準備所有可搜索的字段
            const searchableFields = [
                customer.firstName?.toLowerCase() || '',
                customer.lastName?.toLowerCase() || '',
                customer.email?.toLowerCase() || '',
                customer.phone || '',
                customer.id?.split('/').pop() || '',
                customer.nfc_id?.toLowerCase() || ''
            ];

            // 檢查是否所有搜索詞都能匹配到至少一個字段
            return searchTerms.every(term =>
                searchableFields.some(field => field.includes(term))
            );
        });
    }, [customers]);

    // 排序客戶
    const sortCustomers = useCallback((sortField, sortDirection, data = customers) => {
        if (!sortField) return data;

        return [...data].sort((a, b) => {
            const valueA = a[sortField] || '';
            const valueB = b[sortField] || '';

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return sortDirection === 'asc'
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            }

            return sortDirection === 'asc'
                ? valueA - valueB
                : valueB - valueA;
        });
    }, [customers]);

    return {
        customers,
        loading,
        error,
        selectedCustomer,
        customerLoading,
        customerError,
        loadCustomers,
        getCustomerById,
        searchCustomers,
        sortCustomers
    };
}

export default useCustomer; 